import { useEffect, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import {
  useScroll,
  ScrollControls,
  useMotion,
  MotionPathControls,
  OrbitControls,
  Box,
  Line,
} from '@react-three/drei'
import { useControls, folder } from 'leva'

import { useSelector } from 'react-redux'

import { CatmullRomCurve3, MathUtils, Path, Vector3 } from 'three'

import cameraJSON from '@/utils/controls/camera.json'
import targetJSON from '@/utils/controls/target.json'

export default function CameraControls({ children }) {
  const { mode } = useSelector((state) => state.progress)

  const [targetCurvePath, setTargetCurvePath] = useState(new Path())
  const [cameraCurvePath, setCameraCurvePath] = useState(new Path())
  const [cameraPathLine, setCameraPathLine] = useState(null)
  const [targetPathLine, setTargetPathLine] = useState(null)

  const targetRef = useRef()

  const { camera, mouse } = useThree()

  const {
    controlType,
    fov,
    near,
    far,
    position,
    showPath,
    mouseOffset,
    showTarget,
    easingFactor,
  } = useControls({
    Camera: folder({
      controlType: {
        value: 'MotionPathControls',
        options: ['OrbitControls', 'MotionPathControls'],
      },
      fov: { value: 75, label: 'FOV', min: 1, max: 100 },
      near: { value: 0.1, label: 'Near', min: 0.1, max: 10 },
      far: { value: 40, label: 'Far', min: 10, max: 1000 },
      position: { value: [14, 14, 14], label: 'Position' },
    }),
    Path: folder({
      showPath: false,
      showTarget: false,
      mouseOffset: { value: 0.5, label: 'mouse offset', min: 0, max: 2 },
      easingFactor: {
        value: 0.05,
        label: 'mouse easing factor',
        step: 0.01,
        min: 0,
        max: 0.1,
      },
    }),
  })

  useEffect(() => {
    camera.position.set(14, 14, 14)
  }, [controlType])

  useEffect(() => {
    camera.fov = fov
    camera.near = near
    camera.far = far
    camera.position.set(position[0], position[1], position[2])
    camera.updateProjectionMatrix()
  }, [fov, near, far, position, camera])

  useEffect(() => {
    const targetVectors = targetJSON.points.map(
      (point) => new Vector3(point.x, point.y, point.z),
    )

    const targetCurve = new CatmullRomCurve3(targetVectors)
    const newTargetCurvePath = new Path()
    newTargetCurvePath.add(targetCurve)
    newTargetCurvePath.autoClose = true

    const targetPosition = newTargetCurvePath.getPointAt(0)
    targetRef.current.position.x = targetPosition?.x
    targetRef.current.position.y = targetPosition?.y
    targetRef.current.position.z = targetPosition?.z

    setTargetCurvePath(newTargetCurvePath)

    setTargetPathLine(<Line points={targetVectors} color="blue" />)

    const cameraVectors = cameraJSON.points.map(
      (point) => new Vector3(point.x, point.y, point.z),
    )

    const cameraCurve = new CatmullRomCurve3(cameraVectors)
    const newCameraCurvePath = new Path()
    newCameraCurvePath.add(cameraCurve)
    newCameraCurvePath.autoClose = true
    setCameraCurvePath(newCameraCurvePath)

    setCameraPathLine(<Line points={cameraVectors} color="red" />)
  }, [])

  function Loop() {
    const data = useScroll()
    const motion = useMotion()

    useFrame(() => {
      if (
        targetCurvePath &&
        mode === 'free' &&
        targetCurvePath.curves.length !== 0
      ) {
        if (data.offset && data.offset > 0 && data.offset < 1) {
          motion.current = data.offset
          const targetPosition = targetCurvePath?.getPointAt(data.offset)
          targetRef.current.position.x = MathUtils.lerp(
            targetRef.current.position.x,
            targetPosition.x + mouse.x * mouseOffset,
            easingFactor,
          )
          targetRef.current.position.y = MathUtils.lerp(
            targetRef.current.position.y,
            targetPosition.y + mouse.y * mouseOffset,
            easingFactor,
          )
          targetRef.current.position.z = MathUtils.lerp(
            targetRef.current.position.z,
            targetPosition.z,
            easingFactor,
          )
        } else {
          const targetPosition = targetCurvePath.getPointAt(0)

          if (targetRef.current && targetPosition) {
            targetRef.current.position.x = MathUtils.lerp(
              targetRef.current.position.x,
              targetPosition.x,
              easingFactor,
            )
            targetRef.current.position.y = MathUtils.lerp(
              targetRef.current.position.y,
              targetPosition.y + mouse?.y * mouseOffset,
              easingFactor,
            )
            targetRef.current.position.z = MathUtils.lerp(
              targetRef.current.position.z,
              targetPosition.z + mouse?.x * mouseOffset,
              easingFactor,
            )
          }
        }
      }
    })
  }

  return (
    <>
      <ScrollControls distance={20} damping={0.25}>
        {children}
        {controlType === 'OrbitControls' && (
          <OrbitControls enabled={mode === 'free'} position={[14, 14, 14]} />
        )}
        {controlType === 'MotionPathControls' && (
          <MotionPathControls
            focus={targetRef}
            damping={0.3}
            focusDamping={0}
            curves={cameraCurvePath.curves}
          >
            <Loop />
          </MotionPathControls>
        )}
        <Box args={[1, 1, 1]} ref={targetRef} visible={showTarget} />
        {showPath && (
          <>
            {cameraPathLine}
            {targetPathLine}
          </>
        )}
      </ScrollControls>
    </>
  )
}
